import {
  type PlaceholderScope,
  availablePlaceholdersGrouped,
} from "@msys/textutils";
import { CollapseSection } from "@msys/ui";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";

interface Props {
  scope: PlaceholderScope;
}

// Renders the placeholders available in `scope` as collapsible groups (one
// section per entity group), in registry order. Group headers and per-row
// descriptions are both translated from the `DocumentReplacements` namespace.
// Small scopes (few placeholders) start expanded so users see everything at a
// glance; large scopes (e.g. quotes) start collapsed to stay scannable.
const EXPAND_WHEN_TOTAL_AT_MOST = 10;

export function GroupedPlaceholdersList({ scope }: Props) {
  const { t } = useTranslate(["OrganisationSettings", "DocumentReplacements"]);
  const groups = Object.entries(availablePlaceholdersGrouped(scope));
  const total = groups.reduce(
    (sum, [, group]) => sum + group.entries.length,
    0
  );
  const isInitiallyExpanded = total <= EXPAND_WHEN_TOTAL_AT_MOST;

  return (
    <Stack direction="column" spacing={2}>
      {groups.map(([groupId, group]) => (
        <CollapseSection
          key={groupId}
          title={t(group.descriptionKey, { ns: "DocumentReplacements" })}
          isInitiallyExpanded={isInitiallyExpanded}
        >
          <Table size="small">
            <colgroup>
              <col width="35%" />
              <col width="65%" />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("Placeholder", { ns: "OrganisationSettings" })}
                </TableCell>
                <TableCell>
                  {t("Description", { ns: "OrganisationSettings" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.entries.map(entry => (
                <TableRow key={entry.id}>
                  <TableCell>{`{{${entry.id}}}`}</TableCell>
                  <TableCell>
                    {t(entry.descriptionKey, { ns: "DocumentReplacements" })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CollapseSection>
      ))}
    </Stack>
  );
}
